import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { waLink } from "../../lib/api";
import { PartFinder } from "./PartFinder";
import { RegSearch } from "./RegSearch";

const LINES = ["Airbags", "that deploy", "on cue."];
const HERO_IMG = "https://images.unsplash.com/photo-1591388156010-dd522151da35?w=1600&q=80";

const MaskedLine = ({ text, i }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${i === 1 ? "text-outline" : ""} ${i === 2 ? "text-[#E11D48]" : ""}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

const TiltFrame = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div onMouseMove={move} onMouseLeave={reset} className="[perspective:1200px]" data-testid="hero-tilt-frame">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0)" }}
          transition={{ duration: 1.3, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="clip-frame-lg relative aspect-[4/5] overflow-hidden bg-[#121820]"
        >
          <img src={HERO_IMG} alt="Steering wheel with driver airbag" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,transparent_15%,rgba(9,13,18,0.9)_80%)]" />
        </motion.div>
        <div style={{ transform: "translateZ(60px)" }} className="absolute -left-6 bottom-10 rounded-sm border border-white/10 bg-[#090D12]/80 px-4 py-3 backdrop-blur-xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">SRS status</p>
          <p className="mt-1 flex items-center gap-2 font-display text-2xl font-bold uppercase">
            <span className="relative flex h-2.5 w-2.5">
              <span className="pulse-ring absolute inset-0 rounded-full bg-[#E11D48]" />
              <span className="relative h-2.5 w-2.5 rounded-full bg-[#E11D48]" />
            </span>
            Ready to fit
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-28 sm:pt-32">
      <motion.div style={{ y: bgY }} className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-[-20%] h-[900px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(225,29,72,0.22),transparent_60%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:72px_72px]" />
      </motion.div>

      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-8 lg:grid-cols-12 lg:px-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="font-mono text-xs uppercase tracking-[0.3em] text-slate-300">
            <span className="text-[#E11D48]">●</span> UK stock · GBP pricing · Part-numbered listings
          </motion.p>
          <h1 data-testid="hero-title" className="mt-6 font-display text-[17vw] font-black uppercase leading-[0.84] tracking-[-0.02em] sm:text-8xl lg:text-[8.5rem]">
            {LINES.map((l, i) => <MaskedLine key={l} text={l} i={i} />)}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg"
          >
            Driver, passenger, knee, curtain and seat airbags plus SRS parts — photographed, part-numbered and ready to ship.
            Buy online or message us to check fitment first.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.8 }} className="mt-10 flex flex-wrap gap-3">
            <Link to="/shop" data-testid="hero-shop-button" className="group flex h-14 items-center gap-3 rounded-full bg-[#E11D48] px-8 font-semibold transition-colors hover:bg-[#F43F5E]">
              Shop airbags <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-button" className="flex h-14 items-center gap-3 rounded-full border border-white/20 px-8 font-semibold transition-colors hover:border-emerald-400 hover:text-emerald-400">
              <MessageCircle className="h-4 w-4" /> WhatsApp us
            </a>
          </motion.div>
        </motion.div>
        <div className="mx-auto w-full max-w-sm lg:col-span-5 lg:max-w-none">
          <TiltFrame />
        </div>
      </div>
      <div className="mx-auto mt-16 max-w-7xl px-4 pb-20 sm:px-8 lg:px-12">
        <RegSearch />
        <PartFinder />
      </div>
    </section>
  );
};
